import { Children, useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { GutterHandle } from './GutterHandle';

/** shared duration (ms) for all Split transitions & timers */
const SPLIT_TRANSITION_MS = 500;

// TODO: Clean this component and get rid of the ChatGPT-isms
export const Split = ({
  children,
  direction = 'horizontal',
  initialSplit = 50,
  minSize = 100,
  onDragEnd,
  showSecondChild = true
}) => {
  const isHorizontal = direction === 'horizontal';

  /* persistent pct state + ref */
  const [firstPctState, setFirstPctState] = useState(() => clamp(initialSplit, 0, 100));
  const firstPctRef = useRef(firstPctState);
  const setFirstPct = pct => {
    firstPctRef.current = pct;
    setFirstPctState(pct);
  };

  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const prevSelectRef = useRef(''); // remember body userSelect

  /* children handling */
  const [firstChild, secondChild] = Children.toArray(children);

  /* keep pct in sync with prop */
  useEffect(() => {
    setFirstPct(clamp(initialSplit, 0, 100));
  }, [initialSplit]);

  /* DRAG: start */
  const startDrag = useCallback(
    event => {
      if (!containerRef.current) return;
      const { width, height } = containerRef.current.getBoundingClientRect();
      const sizePx = isHorizontal ? width : height;
      const startPos = isHorizontal ? event.clientX : event.clientY;
      const startFirstPx = (firstPctRef.current / 100) * sizePx;

      // disable global text selection
      prevSelectRef.current = document.body.style.userSelect;
      document.body.style.userSelect = 'none';
      setIsDragging(true);

      const move = moveEvent => {
        const position = isHorizontal ? moveEvent.clientX : moveEvent.clientY;
        const newFirstPct = clamp(
          startFirstPx + (position - startPos),
          minSize,
          sizePx - minSize
        );
        setFirstPct((newFirstPct / sizePx) * 100);
      };

      const up = () => {
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', up);
        document.body.style.userSelect = prevSelectRef.current;
        setIsDragging(false);
        if (onDragEnd) onDragEnd([firstPctRef.current, 100 - firstPctRef.current]);
      };

      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
    },
    [isHorizontal, minSize, onDragEnd]
  );

  /* gutter-hide timing so animation can finish */
  const [hideGutter, setHideGutter] = useState(!showSecondChild);

  useEffect(() => {
    if (!showSecondChild) {
      const t = setTimeout(() => setHideGutter(true), SPLIT_TRANSITION_MS);
      return () => clearTimeout(t);
    }
    // second pane coming back – show gutter immediately
    setHideGutter(false);
  }, [showSecondChild]);

  /* styles */
  const firstStyle = { flexBasis: showSecondChild ? `${firstPctState}%` : '100%' };
  const secondStyle = { flexBasis: showSecondChild ? `${100 - firstPctState}%` : 0 };

  return (
    <SplitRoot
      ref={containerRef}
      flexDirection={isHorizontal ? 'row' : 'column'}
    >
      <Pane dragging={isDragging} sx={firstStyle}>
        {firstChild}
      </Pane>

      <Gutter
        className="gutter"
        orientation={direction}
        hidden={hideGutter}
        onMouseDown={showSecondChild ? startDrag : undefined}
      >
        {showSecondChild && (
          <GutterHandle 
            isDragging={isDragging} 
            orientation={direction}
          />
        )}
      </Gutter>

      <Pane dragging={isDragging} sx={secondStyle}>
        {secondChild}
      </Pane>
    </SplitRoot>
  );
};

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

const SplitRoot = styled(Box)({
  display: 'flex',
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden'
});

const Pane = styled(Box, { shouldForwardProp: prop => prop !== 'dragging' })(
  ({ dragging, theme }) => ({
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    transition: dragging
      ? 'none'
      : `flex-basis ${SPLIT_TRANSITION_MS}ms ${theme.transitions.easing.easeInOut}`
  })
);

const Gutter = styled(
  Box,
  { shouldForwardProp: prop => !['hidden', 'orientation'].includes(prop) }
)(({ theme, orientation, hidden }) => ({
  flex: '0 0 auto',
  display: hidden ? 'none' : 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: orientation === 'vertical' ? 'row-resize' : 'col-resize',
  zIndex: 1,
  height: orientation === 'vertical' ? theme.spacing(1.25) : '100%',
  width: orientation === 'vertical' ? '100%' : theme.spacing(1.25)
}));
